import { BigNumber } from "bignumber.js";
import { Dispatch as Dispatch_, useReducer } from "react";

import { ZERO } from "@/constants/math";

export type PositionDirection = "long" | "short";
export type TradeToken = "base" | "quote";

export type State = Partial<{
  direction: PositionDirection;
  token: TradeToken;
  leverage: number;
  positionSize: {
    total: BigNumber;
    baseAmount: BigNumber;
    quoteAmount: BigNumber;
    leveraged: BigNumber;
  };
  depositAmount: string;
  depositAmountBn: BigNumber | undefined;
}>;

type Action =
  | { type: "setDirection"; direction: PositionDirection }
  | {
      type: "setToken";
      token: TradeToken;
      basePrice: BigNumber;
      decimals: number;
    }
  | { type: "setLeverage"; leverage: number; basePrice: BigNumber }
  | {
      type: "setDepositAmount";
      depositAmount: State["depositAmount"];
      basePrice?: BigNumber;
    }
  | {
      type: "setBlockTime";
      payload: bigint;
    };

const DEFAULT_STATE: State = {
  direction: "long",
  token: "base",
  leverage: 5,
  depositAmount: "",
  depositAmountBn: undefined,
};

const getPositionSize = ({
  depositAmount,
  leverage,
  direction,
  basePrice,
}: {
  depositAmount: string | undefined;
  leverage?: number;
  direction: State["direction"];
  basePrice: BigNumber | undefined;
}): State["positionSize"] => {
  if (!depositAmount || !basePrice || !leverage) return undefined;
  const depositAmountBn = new BigNumber(depositAmount);

  if (depositAmountBn.isNaN() || !depositAmountBn.isFinite()) return undefined;

  const leveraged = depositAmountBn.times(leverage - 1);

  if (direction === "long") {
    const quoteAmount = basePrice.times(depositAmount).times(leverage - 1);

    return {
      total: leveraged.plus(depositAmount),
      quoteAmount,
      baseAmount: leveraged.plus(depositAmount),
      leveraged,
    };
  }

  const baseAmount = leveraged.div(basePrice);

  return {
    total: leveraged.plus(depositAmount).div(basePrice),
    baseAmount,
    quoteAmount: leveraged.plus(depositAmount),
    leveraged,
  };
};

const flipDepositAmount = (
  basePrice: BigNumber,
  decimals: number,
  token: TradeToken,
  prevDeposit?: string,
) => {
  if (!prevDeposit) {
    return;
  }

  return (
    token === "base"
      ? basePrice.pow(-1).times(prevDeposit)
      : basePrice.times(prevDeposit)
  )
    .dp(decimals)
    .toString(10);
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "setToken": {
      const { token, basePrice, decimals } = action;
      const { token: prevToken, depositAmount: prevDepositAmount } = state;

      const depositAmount =
        token === prevToken
          ? prevDepositAmount
          : flipDepositAmount(basePrice, decimals, token, prevDepositAmount);

      return {
        ...state,
        token,
        depositAmount,
        depositAmountBn: depositAmount ? ZERO.plus(depositAmount) : undefined,
      };
    }
    case "setDirection": {
      const { direction } = action;

      return {
        ...DEFAULT_STATE,
        direction,
        token: direction === "long" ? "base" : "quote",
      };
    }
    case "setLeverage":
      return {
        ...state,
        leverage: action.leverage,
        positionSize: getPositionSize({
          depositAmount: state.depositAmount,
          leverage: action.leverage,
          direction: state.direction,
          basePrice: action.basePrice,
        }),
      };
    case "setDepositAmount":
      const depositAmountBnNext = new BigNumber(action.depositAmount ?? 0);

      return {
        ...state,
        depositAmount: action.depositAmount,
        depositAmountBn: depositAmountBnNext.isFinite()
          ? depositAmountBnNext
          : undefined,
        positionSize: getPositionSize({
          depositAmount: action.depositAmount,
          leverage: state.leverage,
          direction: state.direction,
          basePrice: action.basePrice,
        }),
      };

    default:
      return state;
  }
};

export default function useTradeState() {
  return useReducer(reducer, DEFAULT_STATE);
}

export type Dispatch = Dispatch_<Action>;
